import { useId, type ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { CardHelp, type CardHelpContent } from './CardHelp';
import { cx } from './cx';
import type { ScopedLabel } from './scoped';
import s from './Stat.module.css';

/** A setting the number depends on is missing: say so and link to where it's set. */
export interface NotSetInfo {
  /** "Set it in Executive › Budget pools". */
  message: string;
  actionLabel: string;
  href: string;
}

export type StatTone = 'default' | 'gold' | 'muted';

export interface StatProps {
  /** Scoped by type: "Your awards · since 2025", "Maritime Airborne · cash left 2026". */
  label: ScopedLabel;
  /** Preformatted value with its unit ("$5,000", "3 awards", "0 of 12"). */
  value: string;
  /** Context under the value ("Vest over 4 years", "$0 awarded · full $335,905 available"). */
  note?: string;
  help: CardHelpContent;
  tone?: StatTone;
  /** When set, the tile is greyed and shows "Not set" with the way to set it. */
  notSet?: NotSetInfo;
}

/** A KPI tile. Its label always carries the scope; empty tiles show the true value with context. */
export function Stat({
  label,
  value,
  note,
  help,
  tone = 'default',
  notSet,
}: StatProps): ReactElement {
  const labelId = useId();
  return (
    <section
      aria-labelledby={labelId}
      className={cx(
        s.stat,
        tone === 'gold' && s.gold,
        tone === 'muted' && s.muted,
        notSet && s.notSet,
      )}
    >
      <div className={s.labelRow}>
        <h3 id={labelId} className={cx('eyebrow', s.label)}>
          {label}
        </h3>
        <CardHelp topic={label} shows={help.shows} canDo={help.canDo} />
      </div>
      {notSet ? (
        <>
          <p className={cx('disp', s.value)}>Not set</p>
          <p className={s.note}>{notSet.message}</p>
          <Link href={notSet.href} className={s.notSetLink}>
            {notSet.actionLabel}
          </Link>
        </>
      ) : (
        <>
          <p className={cx('disp', s.value)}>{value}</p>
          {note !== undefined && <p className={s.note}>{note}</p>}
        </>
      )}
    </section>
  );
}
